import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Modal, ModalActions, ModalContent, ModalTitleInverse } from '@c2fo/liquidity';
import { Schedule } from '@/enterprise/features/offerModal/inputs/SchedulingInput';
import { validateCadence } from '@/enterprise/features/offerModal/utils';
import FrequencyInput from '../FrequencyInput';
import { OfferBuilderFrequency, useOfferBuilderContext } from '../OfferBuilderContext';
import { defaultSchedule } from './EditScheduleModal';

export interface EditFrequencyModalProps {
  open: boolean;
  onClose: () => void;
}

const EditFrequencyModal = (props: EditFrequencyModalProps) => {
  const {
    dispatch,
    state: { selected, frequency: contextFrequency, schedule: contextSchedule },
  } = useOfferBuilderContext();
  const { open, onClose } = props;
  const { t } = useTranslation();
  const [internalFrequency, setInternalFrequency] = useState<OfferBuilderFrequency>(contextFrequency ?? 'recurring');
  const [internalSchedule, setInternalSchedule] = useState<Schedule>(contextSchedule);
  const [errors, setErrors] = useState<{ cadence?: { message: string } }>({});

  const hasExistingSchedule = !!(
    internalSchedule.startDate ||
    internalSchedule.endDate ||
    (internalSchedule.cadence.type === 'weekly' && internalSchedule.cadence.value.length > 0)
  );

  const handleSave = () => {
    setErrors({});

    if (internalFrequency === 'recurring' && hasExistingSchedule) {
      const scheduleValidation = validateCadence(internalSchedule.cadence, t);

      if (!scheduleValidation.valid) {
        setErrors({ cadence: { message: scheduleValidation.errorMessage || t('scheduling.selectDayOrReset') } });
        return;
      }
    }

    dispatch({
      type: 'SET_FREQUENCY',
      payload: internalFrequency,
    });

    dispatch({
      type: 'SET_SCHEDULE',
      payload: internalFrequency === 'oneTime' ? defaultSchedule : internalSchedule,
    });

    onClose();
  };

  const handleClose = () => {
    setInternalFrequency(contextFrequency ?? 'recurring');
    setInternalSchedule(contextSchedule);
    setErrors({});
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} className="sm:w-[750px] sm:min-w-0">
      <ModalTitleInverse>
        <span className="flex flex-col">
          <span>{t('builder.editFrequencyModalTitle')}</span>
          <span className="text-sm font-normal text-text-secondary">{t('builder.setStartingRateModalSubtitle')}</span>
        </span>
      </ModalTitleInverse>
      <ModalContent>
        <div className="overflow-hidden rounded-md bg-canvas">
          <div className="flex items-center justify-between border-b-[0.5px] border-stroke p-4">
            <div>{t('builder.frequency')}</div>
            <div className="font-medium">
              {internalFrequency === 'oneTime' ? t('builder.oneTime') : t('builder.recurring')}
            </div>
          </div>
          <div className="bg-blueGray-50">
            <FrequencyInput
              frequency={internalFrequency}
              schedule={internalSchedule}
              hasExistingSchedule={hasExistingSchedule}
              onFrequencyChange={setInternalFrequency}
              onScheduleChange={setInternalSchedule}
              errors={errors}
              includeDescription={false}
              takerMarkets={selected}
            />
          </div>
        </div>
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={handleClose}>
          {t('core.cancel')}
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {t('core.save')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default EditFrequencyModal;
